import { CustomerAccount, User } from "@/api/user"
import { get_display_name } from "@/utils"

function normalize_search(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase()
}

export function filter_customer_owner_options(customers: CustomerAccount[], query: string): CustomerAccount[] {
  const normalized_query = normalize_search(query)
  if (!normalized_query) {
    return customers
  }

  return customers.filter((customer: CustomerAccount) => {
    const searchable = [
      customer.name,
      customer.remote_id,
      customer.promomash_customer ?? "",
      customer.sales_rep ?? "",
    ]
      .map(normalize_search)
      .join(" ")
    return searchable.includes(normalized_query)
  })
}

export function find_customer_owner(customer_id: string, users: User[], editing_user_id: string): User | undefined {
  return users.find((user: User) => {
    if (user.id === editing_user_id) {
      return false
    }
    return user.owned_customers?.some((customer: CustomerAccount) => customer.id === customer_id) ?? false
  })
}

export function customer_owner_transfer_label(customer_id: string, users: User[], editing_user_id: string): string | null {
  const owner = find_customer_owner(customer_id, users, editing_user_id)
  return owner ? `Owned by ${get_display_name(owner)} - selecting transfers ownership` : null
}

export function toggle_customer_id(selected_ids: string[], customer_id: string): string[] {
  if (selected_ids.includes(customer_id)) {
    return selected_ids.filter((id: string) => id !== customer_id)
  }
  return [...selected_ids, customer_id]
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  const customers: CustomerAccount[] = [
    { id: "c1", name: "Wakefern", remote_id: "wakefern", promomash_customer: "Wakefern", sales_rep: "Patrick Domin" },
    { id: "c2", name: "MOMs Organic", remote_id: "moms-organic", promomash_customer: "Mom's Organic Market", sales_rep: "Robert Hamano" },
  ]

  const users: User[] = [
    {
      id: "u1",
      email: "patrick@saffronroad.com",
      first_name: "Patrick",
      last_name: "Domin",
      role: "user",
      org_id: "org",
      owned_customers: [customers[0]],
    },
    {
      id: "u2",
      email: "robert@saffronroad.com",
      first_name: "Robert",
      last_name: "Hamano",
      role: "user",
      org_id: "org",
      owned_customers: [customers[1]],
    },
  ]

  describe("filter_customer_owner_options", () => {
    it("matches customer name, promomash customer, and sales rep", () => {
      expect(filter_customer_owner_options(customers, "wake")).toHaveLength(1)
      expect(filter_customer_owner_options(customers, "organic market")).toHaveLength(1)
      expect(filter_customer_owner_options(customers, "robert")).toHaveLength(1)
    })
  })

  describe("customer_owner_transfer_label", () => {
    it("returns a transfer label for another user's customer", () => {
      expect(customer_owner_transfer_label("c1", users, "u2")).toBe("Owned by Patrick Domin - selecting transfers ownership")
    })

    it("does not return a transfer label for the edited user's current customer", () => {
      expect(customer_owner_transfer_label("c1", users, "u1")).toBeNull()
    })
  })

  describe("toggle_customer_id", () => {
    it("adds and removes ids", () => {
      expect(toggle_customer_id(["c1"], "c2")).toEqual(["c1", "c2"])
      expect(toggle_customer_id(["c1", "c2"], "c1")).toEqual(["c2"])
    })
  })
}
